import { AssetQuote, Behaviour, ClientProposal, ClientQuote, Organisation, Originator } from '@pa/sdk/idf';
import { BehaviourService, mapAttachments } from '@pa/lib-spa';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FeatureFlags, OriginatorType, QuoteStatus, TransactionType } from '@pa/references/idf';
import { Observable, forkJoin, of } from 'rxjs';

import { ActivatedRoute } from '@angular/router';
import { Attachment } from '@pa/lib-spa/types';
import { AuthService } from '../core/auth/auth.service';
import { ConfirmDialogComponent } from '../components/dialogs/confirm-dialog.component';
import { PolicyService } from '../services/policy.service';
import { QuoteService } from '../services/quote.service';
import { SdkService } from './../services/sdk.service';
import { formatDate } from '../utils/date';
import { mapDocumentationLinks } from '../core/utils/attachment.mapping';
import { switchMap } from 'rxjs/operators';

@Component({
    selector: 'app-view-quote',
    templateUrl: './view-quote.component.html',
    styleUrls: ['./view-quote.component.scss'],
})
export class ViewQuoteComponent implements OnInit {
    @ViewChild('confirmDialog') confirmDialog: ConfirmDialogComponent;
    @ViewChild('reassignBrokerDialog') reassignBrokerDialog: ConfirmDialogComponent;
    @ViewChild('responseDialog') responseDialog: ConfirmDialogComponent;

    assetQuotes: AssetQuote[];
    attachments: Attachment[];
    behaviour: Behaviour;
    behaviourId: string = '';
    clientProposal: ClientProposal;
    clientQuote: ClientQuote;
    enabledFeatureFlags: FeatureFlags[];
    organisation: Organisation;
    organisationOriginators: Originator[] = [];
    originator: Originator;
    policyNumber: string;
    proposalId: string = '';
    proposedEffectiveDate: string;
    referralPageLink: string = '';
    selectedOriginatorCode: string;
    serviceActionEvent?: FeatureFlags = undefined;
    timezone: string = 'UTC';
    transactionQuoteName: string = 'Quote';
    formatDate = formatDate;

    error: boolean = false;
    hideReassignBrokerLink: boolean = true;
    hideRegenerateLink: boolean = true;
    hideResendLink: boolean = true;
    isNB: boolean = false;
    isReferral: boolean = false;
    loading: boolean = true;
    loadingAction: boolean = true;
    loadingDetails: boolean = false;
    showBroker: boolean = false;

    constructor(
        private router: ActivatedRoute,
        private quoteService: QuoteService,
        private behaviourService: BehaviourService,
        private policyService: PolicyService,
        private authService: AuthService,
        private sdk: SdkService
    ) {}

    ngOnInit(): void {
        this.loading = true;

        this.router.queryParams
            .pipe(
                switchMap((params) => {
                    this.proposalId = params.proposalId;
                    this.behaviourId = params.behaviourId;

                    return forkJoin([
                        this.quoteService.getBehaviour(this.behaviourId),
                        this.quoteService.getProposal(this.proposalId, this.behaviourId),
                    ]);
                })
            )
            .subscribe({
                next: async ([behaviour, proposal]) => {
                    this.behaviour = behaviour;
                    this.behaviourService.set(behaviour);
                    this.clientProposal = proposal;
                    this.timezone = proposal.timezone ?? 'UTC';

                    this.clientQuote = this.clientProposal.clientQuote;
                    this.originator = this.clientProposal.originator as Originator;
                    this.assetQuotes = this.clientQuote.assetQuotes as AssetQuote[];

                    this.isNB = this.clientProposal.transactionType === TransactionType.newBusiness;
                    this.showBroker = this.originator.type === OriginatorType.intermediary;
                    this.isReferral =
                        [QuoteStatus.dirty, QuoteStatus.filthy].includes(this.clientQuote.status) ||
                        this.clientQuote.referrals?.length > 0 ||
                        this.assetQuotes.some((assetQuote) => assetQuote.referrals.length > 0);

                    this.proposedEffectiveDate = this._getProposedEffectiveDate(this.clientProposal);
                    const documentLinks: Attachment[] = mapDocumentationLinks(this.clientProposal.documentationLinks);
                    this.attachments = mapAttachments(documentLinks, this.behaviour, TransactionType.amendment);

                    if (this.clientProposal.clientPolicy) {
                        const res = await this.policyService
                            .getPolicyHistory(this.clientProposal.clientPolicy, this.behaviourId)
                            .toPromise();
                        this.policyNumber = res?.reference;
                        if (res?.latestRevision) {
                            this.policyNumber += ` Revision ${res.latestRevision}`;
                        }
                    }

                    this.referralPageLink = `/underwriting?proposalId=${this.proposalId}&behaviourId=${this.behaviourId}`;
                    this.transactionQuoteName = this._getTransactionQuoteName();
                    this.loading = false;
                    this.error = false;

                    this._updateServiceActionLinks();
                },
                error: (err) => {
                    this.loading = false;
                    this.error = true;
                },
            });
    }

    formatNameFromEmail(name: string): string {
        if (!name) {
            return '';
        }
        return name.split('@')[0];
    }

    private _getProposedEffectiveDate(clientProposal: ClientProposal): string {
        switch (clientProposal.transactionType) {
            case TransactionType.newBusiness:
                return clientProposal.inceptionDate;
            case TransactionType.amendment:
                return clientProposal.amendmentEffectiveDate;
            case TransactionType.renewal:
                return clientProposal.renewalEffectiveDate;
            case TransactionType.cancellation:
                return clientProposal.cancellationEffectiveDate;
            default:
                const _check: never = clientProposal.transactionType;
                throw new Error('Unknown transaction type: ' + clientProposal.transactionType);
        }
    }

    private _getTransactionQuoteName(): string {
        switch (this.clientProposal.transactionType) {
            case TransactionType.newBusiness:
                return 'Quote';
            case TransactionType.amendment:
                return 'Amendment Offer';
            case TransactionType.renewal:
                return 'Renewal Offer';
            case TransactionType.cancellation:
                return 'Cancellation Offer';
            default:
                const _check: never = this.clientProposal.transactionType;
                return;
        }
    }

    private _updateServiceActionLinks() {
        this.loadingAction = true;
        const invalidQuoteStatus = [QuoteStatus.dirty, QuoteStatus.filthy, QuoteStatus.declined];
        const quoteStatusValid = !invalidQuoteStatus.includes(this.clientQuote.status);
        const isNbRnTransaction = [TransactionType.newBusiness, TransactionType.renewal].includes(
            this.clientProposal.transactionType
        );
        const isProvisioned = this.clientQuote.status === QuoteStatus.provisioned;
        const isUnacceptedExpiredQuote = !isProvisioned && this.clientQuote.expiryDate < new Date().toISOString();

        this.authService.featureFlags
            .pipe(
                switchMap((flags) => {
                    this.enabledFeatureFlags = flags.map((f) => f.type);

                    this.hideResendLink =
                        !this.enabledFeatureFlags.includes(FeatureFlags.insurerResendNotification) ||
                        !quoteStatusValid ||
                        isProvisioned ||
                        isUnacceptedExpiredQuote;

                    if (
                        this.enabledFeatureFlags.includes(FeatureFlags.insurerRegenerateDocuments) ||
                        this.enabledFeatureFlags.includes(FeatureFlags.reAssignBroker)
                    ) {
                        return this.quoteService.getLatestProposal(this.proposalId, this.behaviourId);
                    }

                    return of(undefined);
                })
            )
            .subscribe({
                next: (latestProposal) => {
                    if (latestProposal?._id.toString() === this.clientProposal._id.toString()) {
                        this.hideRegenerateLink =
                            !this.enabledFeatureFlags.includes(FeatureFlags.insurerRegenerateDocuments) ||
                            !quoteStatusValid ||
                            isProvisioned ||
                            isUnacceptedExpiredQuote;

                        this.hideReassignBrokerLink =
                            !this.enabledFeatureFlags.includes(FeatureFlags.reAssignBroker) ||
                            this.originator.type !== OriginatorType.intermediary ||
                            isProvisioned ||
                            !isNbRnTransaction ||
                            isUnacceptedExpiredQuote;
                    }
                    this.loadingAction = false;
                },
                error: () => {
                    this.loadingAction = false;
                },
            });
    }

    openRegenerateAcceptModal() {
        this.serviceActionEvent = FeatureFlags.insurerRegenerateDocuments;
        this.confirmDialog.open(
            `Please confirm you want to Regenerate ${this.transactionQuoteName} Documentation`,
            'Please Confirm!'
        );
        return false;
    }

    openResendAcceptModal() {
        this.serviceActionEvent = FeatureFlags.insurerResendNotification;
        this.confirmDialog.open(
            `Please confirm you want to resend ${this.transactionQuoteName} email`,
            'Please Confirm!'
        );
        return false;
    }

    openReassignBrokerModal() {
        this.serviceActionEvent = FeatureFlags.reAssignBroker;
        const organisationId =
            typeof this.originator.organisation === 'string'
                ? this.originator.organisation
                : this.originator.organisation._id;

        if (this.organisationOriginators?.length === 0) {
            this.loadingDetails = true;
            this.sdk.idf.Organisations.get({ organisationId, originator: 'true' }).then((organisation) => {
                this.organisation = organisation[0];
                this.organisationOriginators = (organisation[0].originators as unknown as Originator[])
                    .filter((originator) => originator.type === OriginatorType.intermediary)
                    .sort((a, b) => (a.individual > b.individual ? 1 : -1));
                this.selectedOriginatorCode = this.organisationOriginators.find(
                    (o) => o.individual === this.originator.individual
                )?.code;
                this.loadingDetails = false;
            });
        }
        this.reassignBrokerDialog.open(`Please confirm who you'd like to Reassign this account to`, 'Reassign Broker');
        return false;
    }

    confirmReassignBroker() {
        this.confirmDialog.open(
            `Once Reassigned, the Broker will automatically receive an updated ${this.transactionQuoteName} Email`,
            ''
        );
    }

    acceptServiceAction(event: FeatureFlags) {
        this.loadingAction = true;

        // Note: accessToken for idf service is already set in app-component.
        let successMessage = 'Success';
        let errorMessage = 'An error occurred - the platform support team has been notified and will be in touch.';
        let eventPromise: Promise<any>;

        if (event === FeatureFlags.insurerRegenerateDocuments) {
            successMessage = `${this.transactionQuoteName} documentation has successfully generated and the ${this.transactionQuoteName} email has been resent`;
            errorMessage = `An error occurred when regenerating the ${this.transactionQuoteName} Documentation - the platform support team has been notified and will be in touch.`;
            eventPromise = this.sdk.idf.Quotes.regenerateDocuments(
                this.behaviourId,
                this.clientProposal.transactionType,
                this.clientProposal._id
            );
        } else if (event === FeatureFlags.insurerResendNotification) {
            successMessage = `${this.transactionQuoteName} Email has successfully been resent.`;
            eventPromise = this.sdk.idf.Quotes.notification(
                this.behaviourId,
                this.clientProposal._id,
                this.originator._id
            );
        } else if (event === FeatureFlags.reAssignBroker) {
            successMessage = `${this.transactionQuoteName} Email has successfully been sent.`;
            eventPromise = this.sdk.idf.Originators.reassignBroker(this.behaviourId, this.selectedOriginatorCode, {
                clientProposal: this.clientProposal._id,
            });
        }

        eventPromise
            .then(() => {
                this.loadingAction = false;
                this.responseDialog.open(successMessage);
            })
            .catch(() => {
                this.loadingAction = false;
                this.responseDialog.open(errorMessage);
            });
    }

    closeClicked() {
        return window.location.reload();
    }
}
